interface StoredAuth {
  token: string;
  refreshToken: string;
  expiresAt: number;
}

// Token storage key
const TOKEN_STORAGE_KEY = 'token';
const REFRESH_TOKEN_STORAGE_KEY = 'refreshToken';
const TOKEN_EXPIRY_KEY = 'token_expires_at';

// Legacy token keys for migration
const LEGACY_TOKEN_KEY = 'auth_token';
const LEGACY_REFRESH_TOKEN_KEY = 'refresh_token';

/**
 * Migrate tokens from legacy keys to new keys
 * This ensures backward compatibility for existing users
 */
const migrateTokens = (): void => {
  try {
    // Check if we need to migrate tokens
    const hasNewToken = localStorage.getItem(TOKEN_STORAGE_KEY) || sessionStorage.getItem(TOKEN_STORAGE_KEY);
    const hasLegacyToken = localStorage.getItem(LEGACY_TOKEN_KEY) || sessionStorage.getItem(LEGACY_TOKEN_KEY);
    
    if (!hasNewToken && hasLegacyToken) {
      
      // Migrate from localStorage
      const legacyToken = localStorage.getItem(LEGACY_TOKEN_KEY);
      const legacyRefreshToken = localStorage.getItem(LEGACY_REFRESH_TOKEN_KEY);
      
      if (legacyToken) {
        localStorage.setItem(TOKEN_STORAGE_KEY, legacyToken);
        localStorage.removeItem(LEGACY_TOKEN_KEY);
      }
      
      if (legacyRefreshToken) {
        localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, legacyRefreshToken);
        localStorage.removeItem(LEGACY_REFRESH_TOKEN_KEY);
      }
      
      // Migrate from sessionStorage
      const legacySessionToken = sessionStorage.getItem(LEGACY_TOKEN_KEY);
      const legacySessionRefreshToken = sessionStorage.getItem(LEGACY_REFRESH_TOKEN_KEY);
      
      if (legacySessionToken) {
        sessionStorage.setItem(TOKEN_STORAGE_KEY, legacySessionToken);
        sessionStorage.removeItem(LEGACY_TOKEN_KEY);
      }
      
      if (legacySessionRefreshToken) {
        sessionStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, legacySessionRefreshToken);
        sessionStorage.removeItem(LEGACY_REFRESH_TOKEN_KEY);
      }
      
    }
  } catch (error) {
    console.error('❌ Failed to migrate tokens:', error);
  }
};

// Run migration on module load
migrateTokens();

/**
 * Store authentication tokens securely
 */
export const storeTokens = (token: string, refreshToken: string, expiresIn: number): void => {
  try {
    const expiresAt = Date.now() + expiresIn * 1000;
    
    // Store tokens
    localStorage.setItem(TOKEN_STORAGE_KEY, token);
    localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, refreshToken);
    localStorage.setItem(TOKEN_EXPIRY_KEY, expiresAt.toString());
  } catch (error) {
    console.error('Failed to store auth tokens:', error);
    throw new Error('Failed to store authentication tokens');
  }
};

/**
 * Retrieve stored authentication data
 */
export const getStoredAuth = (): StoredAuth | null => {
  try {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
    const expiresAt = localStorage.getItem(TOKEN_EXPIRY_KEY);

    if (!token || !refreshToken || !expiresAt) {
      return null;
    }

    return {
      token,
      refreshToken,
      expiresAt: parseInt(expiresAt, 10)
    };
  } catch (error) {
    console.error('Failed to retrieve auth tokens:', error);
    return null;
  }
};

/**
 * Get the stored access token (with automatic cleanup if expired)
 * Note: Only clears the access token on expiry, NOT the refresh token,
 * so the API interceptor can still attempt a token refresh.
 */
export const getStoredToken = (): string | null => {
  try {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (!token) return null;
    
    // Validate token format
    if (!isValidTokenFormat(token)) {
      localStorage.removeItem(TOKEN_STORAGE_KEY);
      localStorage.removeItem(TOKEN_EXPIRY_KEY);
      return null;
    }
    
    // If expired, clear only the access token (preserve refresh token)
    if (isTokenExpired()) {
      localStorage.removeItem(TOKEN_STORAGE_KEY);
      localStorage.removeItem(TOKEN_EXPIRY_KEY);
      return null;
    }
    
    return token;
  } catch (error) {
    console.error('Failed to retrieve access token:', error);
    return null;
  }
};

/**
 * Get the stored refresh token
 */
export const getStoredRefreshToken = (): string | null => {
  try {
    return localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
  } catch (error) {
    console.error('Failed to retrieve refresh token:', error);
    return null;
  }
};

/**
 * Check if the stored token is expired
 */
export const isTokenExpired = (): boolean => {
  try {
    const expiresAt = localStorage.getItem(TOKEN_EXPIRY_KEY);
    if (!expiresAt) return true;

    return Date.now() > parseInt(expiresAt, 10);
  } catch (error) {
    console.error('Failed to check token expiration:', error);
    return true;
  }
};

/**
 * Clear all stored authentication data
 */
export const clearStoredToken = (): void => {
  try {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
    localStorage.removeItem(TOKEN_EXPIRY_KEY);
  } catch (error) {
    console.error('Failed to clear auth tokens:', error);
    throw new Error('Failed to clear authentication tokens');
  }
};

/**
 * Update stored token and its expiration
 */
export const updateStoredToken = (token: string, expiresIn: number): void => {
  try {
    const expiresAt = Date.now() + expiresIn * 1000;
    
    localStorage.setItem(TOKEN_STORAGE_KEY, token);
    localStorage.setItem(TOKEN_EXPIRY_KEY, expiresAt.toString());
  } catch (error) {
    console.error('Failed to update auth token:', error);
    throw new Error('Failed to update authentication token');
  }
};

/**
 * Update stored refresh token (called after token rotation)
 */
export const updateStoredRefreshToken = (refreshToken: string): void => {
  try {
    if (refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, refreshToken);
    }
  } catch (error) {
    console.error('Failed to update refresh token:', error);
    throw new Error('Failed to update refresh token');
  }
};

/**
 * Parse JWT token payload
 */
export const parseJwt = (token: string): Record<string, unknown> => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );

    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Failed to parse JWT token:', error);
    throw new Error('Invalid token format');
  }
};

/**
 * Get time remaining until token expiration in seconds
 */
export const getTokenTimeRemaining = (): number => {
  try {
    const expiresAt = localStorage.getItem(TOKEN_EXPIRY_KEY);
    if (!expiresAt) return 0;

    const remaining = parseInt(expiresAt, 10) - Date.now();
    return Math.max(0, Math.floor(remaining / 1000));
  } catch (error) {
    console.error('Failed to get token time remaining:', error);
    return 0;
  }
};

/**
 * Validate token format
 */
export const isValidTokenFormat = (token: string): boolean => {
  try {
    // Basic JWT format validation
    const parts = token.split('.');
    if (parts.length !== 3) return false;

    // Validate each part is base64url encoded
    // More permissive regex to handle different JWT implementations
    return parts.every(part => /^[A-Za-z0-9-_=]+$/g.test(part));
  } catch (error) {
    console.error('Failed to validate token format:', error);
    return false;
  }
};
